import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  leadSavedViewsApi,
  type SaveViewPayload,
  type UpdateViewPayload
} from "api/lead-saved-views";
import { getErrorMessage } from "utils/error";

const LEAD_SAVED_VIEWS_LIST_KEY = ["LEAD_SAVED_VIEWS", "LIST"] as const;

interface UseLeadSavedViewsParams {
  enabled?: boolean;
}

export const useLeadSavedViews = ({
  enabled = true
}: UseLeadSavedViewsParams = {}) => {
  const queryClient = useQueryClient();

  const invalidateList = () =>
    queryClient.invalidateQueries({ queryKey: LEAD_SAVED_VIEWS_LIST_KEY });

  const { data: views = [], isLoading } = useQuery({
    queryKey: LEAD_SAVED_VIEWS_LIST_KEY,
    queryFn: leadSavedViewsApi.list,
    enabled,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const createView = useMutation({
    mutationKey: ["LEAD_SAVED_VIEWS", "CREATE"],
    mutationFn: (payload: SaveViewPayload) => leadSavedViewsApi.create(payload),
    onSuccess: invalidateList
  });

  // A single PUT serves both rename and overwrite (partial body).
  const updateView = useMutation({
    mutationKey: ["LEAD_SAVED_VIEWS", "UPDATE"],
    mutationFn: ({ id, ...payload }: UpdateViewPayload & { id: number }) =>
      leadSavedViewsApi.update(id, payload),
    onSuccess: invalidateList
  });

  const deleteView = useMutation({
    mutationKey: ["LEAD_SAVED_VIEWS", "DELETE"],
    mutationFn: (id: number) => leadSavedViewsApi.remove(id),
    onSuccess: invalidateList
  });

  return { views, isLoading, createView, updateView, deleteView };
};
